import type { Plugin } from 'vue'
import type { AppProblem } from '@/utils/problem/problem'
import i18n from '@/plugins/i18n'

const DEDUPE_WINDOW_MS = 2500

const lastNotifiedAt = new Map<string, number>()

function isWarningTier(problem: AppProblem): boolean {
  return problem.status === 429 || problem.status === 408 || problem.type === PROBLEM_TYPES.network
}

function fallbackMessage(problem: AppProblem): string {
  const { t } = i18n.global

  if (problem.type === PROBLEM_TYPES.network) {
    return t('problems.network.message')
  }
  if (problem.status === 408) {
    return t('problems.timeout.message')
  }
  if (problem.status === 401 || problem.status === 419) {
    return t('problems.session_expired.message')
  }
  if (problem.status === 429) {
    return t('problems.rate_limited.message')
  }
  if (problem.status === 503) {
    return t('http_error_templates.503.message')
  }
  if (problem.status >= 500) {
    return t('http_error_templates.500.message')
  }

  return t('problems.unknown.message')
}

function baseMessage(problem: AppProblem): string {
  if (problem.title) {
    return problem.detail ? `${problem.title}: ${problem.detail}` : problem.title
  }

  return fallbackMessage(problem)
}

function buildMessage(problem: AppProblem): string {
  const message = baseMessage(problem)

  if (!problem.traceId) {
    return message
  }

  return `${message} ${i18n.global.t('problems.reference', { traceId: problem.traceId })}`
}

export function notifyAmbientProblem(problem: AppProblem): void {
  const now = Date.now()
  const previous = lastNotifiedAt.get(problem.type)
  if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
    return
  }
  lastNotifiedAt.set(problem.type, now)

  const notification = useNotificationStore()
  const variant = isWarningTier(problem) ? 'warning' : 'error'
  const duration = isWarningTier(problem) ? 8000 : 5000

  notification.addNotification(variant, buildMessage(problem), duration)
}

const plugin: Plugin = {
  install() {
    setGlobalProblemHandler(notifyAmbientProblem)
  },
}

export default plugin
